import { Dialog } from "@progress/kendo-react-dialogs";
import { Splitter, type SplitterOnChangeEvent, type SplitterPaneProps } from "@progress/kendo-react-layout";
import { PDFExport } from "@progress/kendo-react-pdf";
import React, { useCallback, useRef, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import ChatConversation from "../../../chat/chatConversation";
import RecentConversationsList from "../../../chat/recentConversationsList";
import { useIntegerQueryParam } from "../../../hooks/query/useQueryParam";
import { selectCurrentCommercialPlan } from "../../../slices/chatSlice";
import CommercialPlanPanel from "../components/commercialPlanPanel";
import SimulationHeading from "../components/simulationHeading";
import SimulationInputs from "../components/simulatorInputs";
import SimulationToolbar from "../components/simulatorToolbar";

const Simulator: React.FC = () => {
    const currentPlan = useAppSelector(selectCurrentCommercialPlan);
    const [showSimulatorInput, setShowSimulatorInput] = useState(false);
    const conversationsListRef = useRef<HTMLDivElement | null>(null);
    const commercialPlanPdfExportRef = useRef<PDFExport | null>(null);

    const [selectedConversationId, setSelectedConversationId] = useIntegerQueryParam("id");

    const [panes, setPanes] = useState<SplitterPaneProps[]>([
        {
            size: "30%",
            min: "240px",
            collapsible: true
        },
        {
            min: "320px",
            collapsible: false
        },
        {
            size: "20%",
            min: "220px",
            collapsible: true
        }
    ]);

    const handleChange = (event: SplitterOnChangeEvent): void => {
        setPanes(event.newState);
    };

    const handleConversationChanged = useCallback((conversationId: number): void => {
        setSelectedConversationId(conversationId, { replace: false });
    }, [setSelectedConversationId]);

    const handleExportCommercialPlan = useCallback((): void => {
        commercialPlanPdfExportRef.current?.save();
    }, []);

    return (
        <>
            <Splitter
                style={{ height: "100%", width: "100%" }}
                panes={panes}
                onChange={handleChange}
            >
                <div className="h-100 d-flex flex-column">
                    <ChatConversation
                        conversationId={selectedConversationId}
                        onConversationChanged={setSelectedConversationId}
                    />
                </div>

                <div className="container d-flex flex-column p-4 mx-auto min-h-0">
                    <SimulationToolbar
                        onExport={handleExportCommercialPlan}
                        onShowSimulatorInput={() => setShowSimulatorInput(true)}
                    />

                    <div className="d-flex flex-column flex-grow-1 min-h-0 gap-4">
                        <CommercialPlanPanel />
                    </div>
                </div>

                <div className="h-100 d-flex flex-column p-1 min-h-0">
                    <RecentConversationsList
                        ref={conversationsListRef}
                        onChange={handleConversationChanged}
                    />
                </div>
            </Splitter>

            {currentPlan && (
                <div
                    aria-hidden="true"
                    style={{
                        left: -10000,
                        pointerEvents: "none",
                        position: "absolute",
                        top: 0,
                        width: 1400
                    }}
                >
                    <PDFExport
                        ref={commercialPlanPdfExportRef}
                        fileName="commercial-plan.pdf"
                        paperSize="A4"
                        landscape={true}
                        margin="0.4in"
                        scale={0.55}
                        repeatHeaders={true}
                    >
                        <div className="commercial-plan-pdf bg-white p-2">
                            <style>
                                /* Hides all the buttons */
                                {`.commercial-plan-pdf button, .commercial-plan-pdf .k-button { display: none !important; }`}
                            </style>
                            <SimulationHeading />
                            <CommercialPlanPanel />
                        </div>
                    </PDFExport>
                </div>
            )}

            {showSimulatorInput && (
                <Dialog title="Simulation Inputs" width={700} onClose={() => setShowSimulatorInput(false)}>
                    <SimulationInputs />
                </Dialog>
            )}
        </>
    );
};

export default Simulator;
